<template>
  <div class="sales-transactions-table">
    <DataTable
      v-model:filters="filters"
      v-model:expandedRows="expandedRows"
      :value="enrichedTransactions"
      dataKey="id"
      filterDisplay="row"
      :globalFilterFields="[
        'counterparty_name',
        'counterparty_country',
        'description',
        'forward_delivery.deal_name',
      ]"
      class="border rounded"
      :scrollable="enrichedTransactions.length > 20"
      :scrollHeight="enrichedTransactions.length > 20 ? '600px' : undefined"
      :sortField="dateColumn.field"
      :sortOrder="dateColumn.sortOrder"
    >
      <template #empty>
        <div class="text-center py-4 text-muted">
          {{ emptyMessage }}
        </div>
      </template>

      <!-- Row Expansion Template -->
      <template #expansion="{ data }">
        <TransactionBreakdownRow :transaction="data" />
      </template>

      <!-- Row Expander Column -->
      <Column expander :headerStyle="{ width: '3rem' }" />

      <!-- ID -->
      <Column
        field="id"
        header="ID"
        sortable
        dataType="numeric"
        :showFilterMatchModes="false"
        :showFilterMenu="false"
        :headerStyle="{ width: '5rem' }"
      >
        <template #sorticon="{ sortOrder }">
          <ColumnSortIcon :sortOrder />
        </template>
        <template #body="{ data }">
          <router-link :to="`/admin/transactions/${data.id}/edit`">
            #{{ data.id }}
          </router-link>
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <InputText
            v-model="filterModel.value"
            type="text"
            @input="filterCallback()"
            class="p-column-filter w-100"
            placeholder="#"
          />
        </template>
      </Column>

      <!-- The date the view is worked by (see dateColumn): the transaction date for spot sales, the
           delivery window for forwards -->
      <Column
        :field="dateColumn.field"
        :header="dateColumn.header"
        sortable
        :showFilterMatchModes="false"
        :showFilterMenu="false"
        :headerStyle="{ width: dateColumn.width }"
        class="wrap-text"
      >
        <template #sorticon="{ sortOrder }">
          <ColumnSortIcon :sortOrder />
        </template>
        <template #body="{ data }">
          {{ dateColumn.text(data) }}
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <ColumnMultiSelectFilter
            v-model="filterModel.value"
            :options="dateColumn.options"
            searchable
            :placeholder="dateColumn.placeholder"
            activeLabel="Active"
            @filter="filterCallback()"
          />
        </template>
      </Column>

      <!-- Counterparty -->
      <Column
        field="counterparty_name"
        header="Counterparty"
        sortable
        :showFilterMatchModes="false"
        :showFilterMenu="false"
        :headerStyle="{ width: '11rem' }"
        class="wrap-text"
      >
        <template #sorticon="{ sortOrder }">
          <ColumnSortIcon :sortOrder />
        </template>
        <template #body="{ data }">
          {{ data.counterparty_name ?? "N/A" }}
          {{
            data.counterparty_country ? `(${data.counterparty_country})` : ""
          }}
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <ColumnMultiSelectFilter
            v-model="filterModel.value"
            :options="uniqueCounterparties"
            searchable
            placeholder="Filter"
            activeLabel="Active"
            @filter="filterCallback()"
          />
        </template>
      </Column>

      <!-- Deal (forwards view only): the delivery's deal, linking to it -->
      <Column
        v-if="showDeal"
        field="forward_delivery.deal_name"
        header="Deal"
        sortable
        :showFilterMatchModes="false"
        :showFilterMenu="false"
        :headerStyle="{ width: '12rem' }"
        class="wrap-text"
      >
        <template #sorticon="{ sortOrder }">
          <ColumnSortIcon :sortOrder />
        </template>
        <template #body="{ data }">
          <template v-if="data.forward_delivery">
            <router-link
              :to="
                dealRoute(
                  data.forward_delivery.deal_id,
                  data.forward_delivery.delivery_id
                )
              "
            >
              {{ data.forward_delivery.deal_name }}
            </router-link>
          </template>
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <InputText
            v-model="filterModel.value"
            type="text"
            @input="filterCallback()"
            class="p-column-filter w-100"
            placeholder="Filter"
          />
        </template>
      </Column>

      <!-- Country -->
      <Column
        field="counterparty_country"
        header="Country"
        sortable
        :showFilterMatchModes="false"
        :showFilterMenu="false"
        :headerStyle="{ width: '7rem' }"
      >
        <template #sorticon="{ sortOrder }">
          <ColumnSortIcon :sortOrder />
        </template>
        <template #body="{ data }">
          {{ data.counterparty_country ?? "N/A" }}
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <ColumnMultiSelectFilter
            v-model="filterModel.value"
            :options="uniqueCountries"
            placeholder="Filter"
            activeLabel="Active"
            @filter="filterCallback()"
          />
        </template>
      </Column>

      <!-- EECS Account Number -->
      <Column
        field="counterparty_eecs_account_number"
        header="EECS Account"
        sortable
        :showFilterMatchModes="false"
        :showFilterMenu="false"
        :headerStyle="{ width: '9rem' }"
      >
        <template #sorticon="{ sortOrder }">
          <ColumnSortIcon :sortOrder />
        </template>
        <template #body="{ data }">
          {{ data.counterparty_eecs_account_number ?? "N/A" }}
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <InputText
            v-model="filterModel.value"
            type="text"
            @input="filterCallback()"
            class="p-column-filter w-100"
            placeholder="Search..."
          />
        </template>
      </Column>

      <!-- Amount -->
      <Column
        field="amount"
        header="Amount"
        sortable
        dataType="numeric"
        :showFilterMatchModes="false"
        :showFilterMenu="false"
        :headerStyle="{ width: '7rem' }"
      >
        <template #sorticon="{ sortOrder }">
          <ColumnSortIcon :sortOrder />
        </template>
        <template #body="{ data }">
          <div class="text-end">
            {{ data.amount.toLocaleString() }}
          </div>
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <ColumnMultiSelectFilter
            v-model="filterModel.value"
            :options="uniqueAmounts"
            searchable
            placeholder="Filter"
            activeLabel="Active"
            @filter="filterCallback()"
          />
        </template>
      </Column>

      <!-- Unit Price -->
      <Column
        field="unit_price"
        header="Unit Price"
        sortable
        :sortField="unitPriceSortField"
        :showFilterMatchModes="false"
        :showFilterMenu="false"
        :headerStyle="{ width: '7rem' }"
      >
        <template #sorticon="{ sortOrder }">
          <ColumnSortIcon :sortOrder />
        </template>
        <template #body="{ data }">
          {{ data.unit_price ?? "N/A" }}
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <ColumnMultiSelectFilter
            v-model="filterModel.value"
            :options="uniqueUnitPrices"
            searchable
            placeholder="Filter"
            activeLabel="Active"
            @filter="filterCallback()"
          />
        </template>
      </Column>

      <!-- Total -->
      <Column
        field="total_price"
        header="Total"
        sortable
        :sortField="totalPriceSortField"
        :showFilterMatchModes="false"
        :showFilterMenu="false"
        :headerStyle="{ width: '8rem' }"
      >
        <template #sorticon="{ sortOrder }">
          <ColumnSortIcon :sortOrder />
        </template>
        <template #body="{ data }">
          <div class="text-end">
            {{
              data.total_price ? formatPrice(Number(data.total_price)) : "N/A"
            }}
          </div>
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <ColumnMultiSelectFilter
            v-model="filterModel.value"
            :options="uniqueTotals"
            searchable
            placeholder="Filter"
            activeLabel="Active"
            @filter="filterCallback()"
          />
        </template>
      </Column>

      <!-- Total Fees -->
      <Column
        field="total_fees_charged"
        header="Total Fees"
        sortable
        :sortField="totalFeesSortField"
        :showFilterMatchModes="false"
        :showFilterMenu="false"
        :headerStyle="{ width: '8rem' }"
      >
        <template #sorticon="{ sortOrder }">
          <ColumnSortIcon :sortOrder />
        </template>
        <template #body="{ data }">
          <div class="text-end">
            {{
              data.total_fees_charged != null
                ? `${formatMoney(Number(data.total_fees_charged))} €`
                : "N/A"
            }}
          </div>
        </template>
      </Column>

      <!-- Status (pending only) -->
      <Column
        v-if="showActions"
        field="status"
        header="State"
        sortable
        :showFilterMatchModes="false"
        :showFilterMenu="false"
        :headerStyle="{ width: '10rem' }"
      >
        <template #sorticon="{ sortOrder }">
          <ColumnSortIcon :sortOrder />
        </template>
        <template #body="{ data }">
          <span class="badge" :class="getStatusClass(data.status)">
            {{ data.status }}
          </span>
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <ColumnMultiSelectFilter
            v-model="filterModel.value"
            :options="uniqueStatuses"
            placeholder="Filter"
            activeLabel="Active"
            @filter="filterCallback()"
          >
            <template #option="{ option }">
              <span class="badge" :class="getStatusClass(option.label)">
                {{ option.label }}
              </span>
            </template>
          </ColumnMultiSelectFilter>
        </template>
      </Column>

      <!-- Description -->
      <Column
        field="description"
        header="Description"
        sortable
        :showFilterMatchModes="false"
        :showFilterMenu="false"
        :headerStyle="{ width: '10rem' }"
        class="wrap-text"
      >
        <template #sorticon="{ sortOrder }">
          <ColumnSortIcon :sortOrder />
        </template>
        <template #body="{ data }">
          {{ data.description ?? "N/A" }}
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <InputText
            v-model="filterModel.value"
            type="text"
            @input="filterCallback()"
            class="p-column-filter w-100"
            placeholder="Search..."
          />
        </template>
      </Column>

      <!-- Buyer Invoice (completed only) -->
      <Column
        v-if="showBuyerInvoiceStatus"
        field="buyer_invoice_state"
        header="Buyer Invoice"
        sortable
        :showFilterMatchModes="false"
        :showFilterMenu="false"
        :headerStyle="{ width: '10rem' }"
      >
        <template #sorticon="{ sortOrder }">
          <ColumnSortIcon :sortOrder />
        </template>
        <template #body="{ data }">
          <template
            v-if="getBuyerInvoiceDate(data) || data.buyer_sales_invoice_sent_at"
          >
            <span
              v-if="getBuyerInvoiceDate(data)"
              class="badge"
              :class="getBuyerInvoiceBadgeClass(getBuyerInvoiceState(data))"
              :title="getBuyerInvoiceStateLabel(data)"
            >
              {{ formatDateTimeIntoDate(getBuyerInvoiceDate(data) as string) }}
            </span>
            <div
              v-if="getBuyerInvoiceLastSend(data)"
              class="small text-muted"
              :title="getBuyerInvoiceLastSend(data)?.title"
            >
              {{ getBuyerInvoiceLastSend(data)?.label }}
              {{ formatDateTimeIntoDate(getBuyerInvoiceLastSend(data)?.date) }}
            </div>
          </template>
          <template v-else>N/A</template>
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <MultiSelect
            v-model="filterModel.value"
            :options="buyerInvoiceStateOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Filter"
            class="p-column-filter w-100"
            display="chip"
            @change="filterCallback()"
            @selectall-change="filterCallback()"
          >
            <template #filtericon>
              <font-awesome-icon
                icon="search"
                class="text-muted pv-multiselect-filter-icon"
              />
            </template>
            <template #value="{ value, placeholder }">
              <span v-if="value && value.length" class="text-primary fw-bold">
                Active
              </span>
              <span v-else>{{ placeholder }}</span>
            </template>
            <template #option="slotProps">
              <span
                class="badge"
                :class="getBuyerInvoiceBadgeClass(slotProps.option.value)"
              >
                {{ slotProps.option.label }}
              </span>
            </template>
          </MultiSelect>
        </template>
      </Column>

      <!-- Actions (pending only) -->
      <Column
        v-if="showActions"
        header="Actions"
        :headerStyle="{ width: '11rem' }"
      >
        <template #body="{ data }">
          <slot name="actions" :transaction="data"></slot>
        </template>
      </Column>
    </DataTable>
  </div>
</template>

<script setup lang="ts">
import { FilterMatchMode, FilterService } from "@primevue/core/api";
import Column from "primevue/column";
import DataTable from "primevue/datatable";
import InputText from "primevue/inputtext";
import MultiSelect from "primevue/multiselect";
import { computed, ref } from "vue";

import { formatDateTimeIntoDate, formatMoney } from "@/composables/formatUtils";
import { dealRoute } from "@/forwards2/deals/dealRoute";
import { useDeliveryWindowText } from "@/forwards2/deals/useDeliveryWindowText";
import type { ForwardDeliveryHeader } from "@/forwards2/forwardsTypes";
import {
  deadlineMonthOptions,
  getBuyerInvoiceLastSend,
} from "@/money/admin/transactions/transactionsListViewHelpers";
import { SalesTransaction } from "@/types";
import TransactionBreakdownRow from "./components/TransactionBreakdownRow.vue";
import { DEADLINE_MONTH_MATCH_MODE } from "@/utils/primevueFilters";
import ColumnMultiSelectFilter from "@/components/common/ColumnMultiSelectFilter.vue";
import ColumnSortIcon from "@/components/common/ColumnSortIcon.vue";

const props = withDefaults(
  defineProps<{
    transactions: SalesTransaction[];
    showActions?: boolean;
    showBuyerInvoiceStatus?: boolean;
    /** Add the deal column; for the forwards view, where every row is a delivery. */
    showDeal?: boolean;
    emptyMessage?: string;
  }>(),
  {
    showActions: false,
    showBuyerInvoiceStatus: false,
    showDeal: false,
    emptyMessage: "No transactions found.",
  }
);

const expandedRows = ref<Record<number, boolean>>({});

// The deal page's wording for a delivery window, fed from the header's field names.
const deliveryWindowText = useDeliveryWindowText();
const windowText = (delivery: ForwardDeliveryHeader) =>
  deliveryWindowText({
    delivery_window_start: delivery.window_start,
    delivery_window_end: delivery.window_end,
  });

const formatPrice = (price: number): string => {
  return price.toFixed(3);
};

type BuyerInvoiceState = "PAID" | "OVERDUE" | "PENDING" | "NONE";

const getBuyerInvoiceState = (t: SalesTransaction): BuyerInvoiceState => {
  if (t.buyer_sales_invoice_paid_at) return "PAID";
  if (!t.buyer_sales_invoice_due_at) return "NONE";
  return t.buyer_sales_invoice_is_overdue ? "OVERDUE" : "PENDING";
};

// Pending/overdue invoices show the payment due date; paid ones show the actual payment date.
const getBuyerInvoiceDate = (t: SalesTransaction): string | null => {
  if (t.buyer_sales_invoice_paid_at) return t.buyer_sales_invoice_paid_at;
  if (t.buyer_sales_invoice_due_at) return t.buyer_sales_invoice_due_at;
  return null;
};

const getBuyerInvoiceBadgeClass = (state: BuyerInvoiceState): string => {
  switch (state) {
    case "PAID":
      return "bg-success";
    case "OVERDUE":
      return "bg-danger";
    case "PENDING":
      return "bg-warning text-dark";
    default:
      return "bg-secondary";
  }
};

const BUYER_INVOICE_STATE_LABELS: Record<BuyerInvoiceState, string> = {
  PAID: "Paid",
  OVERDUE: "Overdue",
  PENDING: "Pending",
  NONE: "No invoice",
};

const getBuyerInvoiceStateLabel = (t: SalesTransaction): string =>
  BUYER_INVOICE_STATE_LABELS[getBuyerInvoiceState(t)];

const getStatusClass = (status: string): string => {
  switch (status) {
    case "DRAFT":
      return "bg-secondary";
    case "PENDING_BUYER_DETAILS":
      return "bg-info";
    case "PENDING_CONTRACTS":
      return "bg-warning text-dark";
    case "READY_TO_TRANSFER":
      return "bg-primary";
    case "DELIVERED":
      return "bg-info";
    case "COMPLETED":
      return "bg-success";
    default:
      return "bg-secondary";
  }
};

const unitPriceSortField = ((item: SalesTransaction) =>
  item.unit_price != null
    ? parseFloat(item.unit_price)
    : -1) as unknown as string;

const totalPriceSortField = ((item: SalesTransaction) =>
  item.total_price != null
    ? parseFloat(item.total_price)
    : -1) as unknown as string;

const totalFeesSortField = ((item: SalesTransaction) =>
  item.total_fees_charged != null
    ? parseFloat(item.total_fees_charged)
    : -1) as unknown as string;

// ID filter: substring match on stringified id
FilterService.register(
  "idContains",
  (value: number | null, filter: string | null): boolean => {
    if (!filter) return true;
    if (value == null) return false;
    return String(value).includes(filter);
  }
);

// Date filter: match by formatted date (date portion only) so transactions
// on the same calendar day with different timestamps group together.
FilterService.register(
  "dateIn",
  (value: string | null, filters: string[]): boolean => {
    if (!filters || !filters.length) return true;
    if (!value) return false;
    const formatted = formatDateTimeIntoDate(value);
    return filters.includes(formatted);
  }
);

const filters = ref({
  global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  id: { value: null, matchMode: "idContains" as any },
  counterparty_name: { value: [], matchMode: FilterMatchMode.IN },
  counterparty_country: { value: [], matchMode: FilterMatchMode.IN },
  "forward_delivery.deal_name": {
    value: null,
    matchMode: FilterMatchMode.CONTAINS,
  },
  "forward_delivery.window_end": {
    value: [],
    matchMode: DEADLINE_MONTH_MATCH_MODE,
  },
  counterparty_eecs_account_number: {
    value: null,
    matchMode: FilterMatchMode.CONTAINS,
  },
  transaction_at: { value: [], matchMode: "dateIn" as any },
  amount: { value: [], matchMode: FilterMatchMode.IN },
  unit_price: { value: [], matchMode: FilterMatchMode.IN },
  total_price: { value: [], matchMode: FilterMatchMode.IN },
  status: { value: [], matchMode: FilterMatchMode.IN },
  description: { value: null, matchMode: FilterMatchMode.CONTAINS },
  buyer_invoice_state: { value: [], matchMode: FilterMatchMode.IN },
});

type EnrichedSalesTransaction = SalesTransaction & {
  buyer_invoice_state: BuyerInvoiceState;
};

const enrichedTransactions = computed<EnrichedSalesTransaction[]>(() =>
  props.transactions.map((t) => ({
    ...t,
    buyer_invoice_state: getBuyerInvoiceState(t),
  }))
);

const buyerInvoiceStateOptions = computed(() => {
  const states = new Set<BuyerInvoiceState>();
  for (const t of enrichedTransactions.value) {
    states.add(t.buyer_invoice_state);
  }
  const order: BuyerInvoiceState[] = ["OVERDUE", "PENDING", "PAID", "NONE"];
  return order
    .filter((s) => states.has(s))
    .map((s) => ({ label: BUYER_INVOICE_STATE_LABELS[s], value: s }));
});

const uniqueCounterparties = computed(() => {
  const names = new Set<string>();
  for (const t of props.transactions) {
    if (t.counterparty_name) names.add(t.counterparty_name);
  }
  return [...names].sort().map((v) => ({ label: v, value: v }));
});

const uniqueCountries = computed(() => {
  const countries = new Set<string>();
  for (const t of props.transactions) {
    if (t.counterparty_country) countries.add(t.counterparty_country);
  }
  return [...countries].sort().map((v) => ({ label: v, value: v }));
});

const uniqueDates = computed(() => {
  const dateMap = new Map<string, string>();
  for (const t of props.transactions) {
    if (t.transaction_at) {
      const formatted = formatDateTimeIntoDate(t.transaction_at);
      if (!dateMap.has(formatted)) {
        dateMap.set(formatted, t.transaction_at);
      }
    }
  }
  return [...dateMap.entries()]
    .sort((a, b) => b[1].localeCompare(a[1]))
    .map(([label]) => ({ label, value: label }));
});

const deadlineMonths = computed(() => deadlineMonthOptions(props.transactions));

// The column after ID is the date each view is worked by. Spot sales: the transaction date, newest
// first. Forward deliveries: the delivery window, whose end is the transfer deadline, soonest first.
// It also sets the table's initial sort — a plain initial value, since the view switch remounts
// the table.
const dateColumn = computed(() =>
  props.showDeal
    ? {
        field: "forward_delivery.window_end",
        header: "Delivery window",
        width: "12rem",
        placeholder: "Deadline month",
        options: deadlineMonths.value,
        sortOrder: 1,
        text: (t: SalesTransaction) =>
          t.forward_delivery ? windowText(t.forward_delivery) : "N/A",
      }
    : {
        field: "transaction_at",
        header: "Date",
        width: "8rem",
        placeholder: "Filter",
        options: uniqueDates.value,
        sortOrder: -1,
        text: (t: SalesTransaction) =>
          t.transaction_at ? formatDateTimeIntoDate(t.transaction_at) : "N/A",
      }
);

const uniqueStatuses = computed(() => {
  const statuses = new Set<string>();
  for (const t of props.transactions) {
    if (t.status) statuses.add(t.status);
  }
  return [...statuses].sort().map((v) => ({ label: v, value: v }));
});

const uniqueAmounts = computed(() => {
  const amounts = new Set<number>();
  for (const t of props.transactions) {
    amounts.add(t.amount);
  }
  return [...amounts]
    .sort((a, b) => a - b)
    .map((v) => ({ label: v.toLocaleString(), value: v }));
});

const uniqueUnitPrices = computed(() => {
  const prices = new Set<string>();
  for (const t of props.transactions) {
    if (t.unit_price) prices.add(t.unit_price);
  }
  return [...prices]
    .sort((a, b) => parseFloat(a) - parseFloat(b))
    .map((v) => ({ label: v, value: v }));
});

const uniqueTotals = computed(() => {
  const totals = new Set<string>();
  for (const t of props.transactions) {
    if (t.total_price) totals.add(t.total_price);
  }
  return [...totals]
    .sort((a, b) => parseFloat(a) - parseFloat(b))
    .map((v) => ({ label: formatPrice(Number(v)), value: v }));
});
</script>

<style scoped lang="scss">
.sales-transactions-table {
  overflow-x: auto;
}
</style>
